/**
 * CSS-language syntax colours for the colour tool's code block, for themes whose
 * palette is NOT published as a public static asset:
 *
 *  - DEFAULT, used when no IDE-skin theme is active. A readable, Codigrate-brand
 *               (blue / cyan / green) scheme on a fixed dark code surface, so the
 *               snippet never falls back to the washed-out accent ramp.
 *  - PREMIUM, the eight themes that ship no public palette.json. Their colours
 *               are derived from the product designTokens and bundled here on
 *               purpose: premium themes must not expose a downloadable palette
 *               file under /assets. The twelve free themes keep reading their
 *               public palette.json.
 *
 * Roles map onto the code block as: selector (:root/.foo), property (custom
 * props + property names), value (hex/number/unit), keyword (at-rules/important),
 * comment, punctuation.
 */
export interface CssSyntaxTheme {
  editorBackground: string;
  editorForeground: string;
  comment: string;
  selector: string;
  property: string;
  value: string;
  keyword: string;
  punctuation: string;
}

// Emergency fallback only (used if the default palette JSON fails to load). The
// live default is fetched per light/dark mode from
// assets/themes/default/default-{light,dark}.palette.json. Editor bg/fg are left
// empty so the code block uses the site --background-color / --color (editor
// background == page background); only the Codigrate blue/cyan syntax is set.
export const CSS_SYNTAX_DEFAULT: CssSyntaxTheme = {
  editorBackground: '',
  editorForeground: '',
  comment: '#8A929E',
  selector: '',
  property: '#4DD2FF',
  value: '#5CF0F0',
  keyword: '#4DD2FF',
  punctuation: '',
};

// All values are the premium theme's real JetBrains colours, pulled from
// all-in-one-themes (the premium .xml live there): selector = HTML_TAG_NAME,
// property = DEFAULT_ATTRIBUTE (markup), value = DEFAULT_NUMBER, keyword =
// CSS.IMPORTANT/DEFAULT_KEYWORD, comment = DEFAULT_LINE_COMMENT, editor +
// punctuation from the editor surface. Bundled (not a public palette.json)
// because these are premium themes.
export const CSS_SYNTAX_PREMIUM: { [ key: string ]: CssSyntaxTheme } = {
  madrid: {
    editorBackground: '#FBFAF2', editorForeground: '#372805', comment: '#7190A6',
    selector: '#BD8712', property: '#5A91A8', value: '#BD503F', keyword: '#B84440', punctuation: '#372805',
  },
  prague: {
    editorBackground: '#0D1D2E', editorForeground: '#BBC9D7', comment: '#92A3B3',
    selector: '#DF865B', property: '#E3CA77', value: '#89C5F8', keyword: '#74ADF8', punctuation: '#BBC9D7',
  },
  sydney: {
    editorBackground: '#F6F9FA', editorForeground: '#062E3A', comment: '#9C9378',
    selector: '#C18C47', property: '#B58B22', value: '#2F6781', keyword: '#2E89A6', punctuation: '#062E3A',
  },
  vienna: {
    editorBackground: '#2C1D1D', editorForeground: '#E6D7D8', comment: '#B6A498',
    selector: '#E8997C', property: '#F3CD86', value: '#F4D8B3', keyword: '#DCA826', punctuation: '#E6D7D8',
  },
  ocean: {
    editorBackground: '#102B2E', editorForeground: '#AFBABD', comment: '#96B9B5',
    selector: '#5AB57E', property: '#DC9577', value: '#69A6F8', keyword: '#3FB4D8', punctuation: '#AFBABD',
  },
  reynisfjara: {
    editorBackground: '#151A20', editorForeground: '#AEB6C0', comment: '#8C838C',
    selector: '#B1AC9B', property: '#91AC8E', value: '#AEB8C5', keyword: '#AC8563', punctuation: '#AEB6C0',
  },
  salda: {
    editorBackground: '#FDFCFA', editorForeground: '#312408', comment: '#868888',
    selector: '#3F7FAE', property: '#9E7363', value: '#1397AE', keyword: '#44849F', punctuation: '#312408',
  },
  spring: {
    editorBackground: '#FAFFF9', editorForeground: '#122C07', comment: '#919063',
    selector: '#55956B', property: '#BD7070', value: '#8C68AC', keyword: '#BA6173', punctuation: '#122C07',
  },
};
